/**
 * What the Worker terminal panel says, decided away from the panel itself.
 *
 * Pure text in, pure text out: the words the owner reads about a terminal.
 * Kept here so they can be tested without a browser, and so the components stay about drawing.
 */
import type { TerminalActionRow, WorkerTerminalRow } from './data';

export type Attention = 'waiting' | 'error' | 'working' | 'quiet';

export interface TerminalInputAccess {
  readonly writable: boolean;
  readonly canTakeControl: boolean;
  readonly holder: string | null;
  readonly explanation: string;
}

/**
 * Whether direct xterm input belongs to the owner right now.
 *
 * A machine-held keyboard is never taken merely because the owner clicked or pressed a key.
 * The explicit Take control action releases the canonical input hold first.
 */
export function terminalInputAccess(row: WorkerTerminalRow): TerminalInputAccess {
  if (row.closed_at !== null || row.failure !== null || ['closed', 'failed'].includes(row.terminal_status ?? '')) {
    return {
      writable: false,
      canTakeControl: false,
      holder: null,
      explanation: 'This terminal has ended and cannot accept input.',
    };
  }

  const ownerHasKeyboard = row.input_holder === 'owner_terminal' || row.input_holder === 'owner_composer';
  if (row.input_held !== true || ownerHasKeyboard) {
    return {
      writable: true,
      canTakeControl: false,
      holder: null,
      explanation: 'Keyboard · Yours · Click the terminal to type.',
    };
  }

  const holder = row.input_holder_controller_name?.trim()
    || row.input_holder?.replaceAll('_', ' ')
    || 'Another controller';
  return {
    writable: false,
    canTakeControl: true,
    holder,
    explanation: `${holder} has keyboard control. Take control before typing.`,
  };
}

/**
 * #736: the runtime a Worker is pinned to, or null when it is not actually pinned.
 * Auto, a task override, Unknown, or a pin whose runtime is not known is never a pin here.
 */
export function pinnedRuntimeOf(profile: {
  readonly mode: string; readonly configured_runtime_key: string | null;
} | null): string | null {
  if (profile?.mode !== 'pinned') return null;
  return profile.configured_runtime_key?.trim() || 'Brunel\'s configured runtime';
}

/**
 * #736: said before the owner opens a terminal by hand on a Worker pinned to a runtime.
 * Null when there is no pin to warn about.
 */
export function manualShellOpenWarning(pinnedRuntime: string | null): string | null {
  if (pinnedRuntime === null) return null;
  return `This Worker is pinned to ${pinnedRuntime}. Opening a terminal here starts a manual shell, not ${pinnedRuntime}. `
    + `A Worker has one terminal, so while this shell stays open Brunel's dispatch is handed it instead of starting ${pinnedRuntime}, `
    + 'and tasks for this Worker can be blocked. Hand it back to Brunel when you are done.';
}

/**
 * #736: true only when Brunel recorded that the owner opened this live terminal by hand.
 * A runtime terminal, or one whose origin is Unknown, is never offered for handback.
 */
export function isConfirmedManualShell(row: WorkerTerminalRow | null): boolean {
  return row !== null && row.closed_at === null && row.terminal_origin === 'owner_manual';
}

/** #736: what the owner is told after a manual shell is handed back. */
export const MANUAL_SHELL_HANDED_BACK = 'Manual shell close requested. Once it has ended, Brunel\'s normal dispatch can open this Worker\'s runtime terminal for its next task.';

/** What the terminal's own state means for the person looking at it. */
export function attentionFor(row: WorkerTerminalRow): { readonly tone: Attention; readonly line: string } {
  if (row.failure !== null) return { tone: 'error', line: `The terminal ended with an error: ${row.failure}` };
  if (row.closed_at !== null) {
    return { tone: 'error', line: row.exit_code === null ? 'The terminal has ended.' : `The terminal ended (${row.exit_code}).` };
  }
  switch (row.terminal_status) {
    case 'waiting_for_approval': return { tone: 'waiting', line: 'The tool is asking for permission. Answer it in the terminal.' };
    case 'waiting_for_input': return { tone: 'waiting', line: 'The tool is waiting for something to be typed.' };
    case 'idle': return { tone: 'quiet', line: 'Nothing is running; the terminal is at its prompt.' };
    case 'running': return { tone: 'working', line: 'Something is running.' };
    default: return { tone: 'quiet', line: 'Waiting for the computer to pick this terminal up.' };
  }
}

/** One line for an action, in words: who did what, and what was decided about it. */
export function actionLine(action: TerminalActionRow): string {
  const who = action.source.startsWith('owner_')
    ? `you, at the ${action.source.slice('owner_'.length)}`
    : action.source.replaceAll('_', ' ');
  const what = action.action === 'write'
    ? `typed ${action.byte_count} character${action.byte_count === 1 ? '' : 's'}`
    : action.action === 'interrupt' ? 'interrupted the program'
    : action.action === 'resize' ? 'resized the terminal' : 'closed the terminal';
  const decided = action.decision === 'allow' ? '' : ` - ${action.decision.replaceAll('_', ' ')}`;
  return `${who}: ${what}${decided}`;
}
